import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { buildRoutePath, routes } from '../../constants/routes'

// Collage placeholders and KI shapes:
import { ReactComponent as KiBgSvgOne } from '../../assets/svgs/ki-shape-01.svg'
import { ReactComponent as KiBgSvgTwo } from '../../assets/svgs/ki-shape-02.svg'
import { ReactComponent as KiBgSvgThree } from '../../assets/svgs/ki-shape-03.svg'
import { ReactComponent as KiBgSvgFour } from '../../assets/svgs/ki-shape-04.svg'
import { ReactComponent as KiBgSvgFive } from '../../assets/svgs/ki-shape-05.svg'
import { ReactComponent as GrayBgSvgOne } from '../../assets/svgs/clayville-collagebgshapes-mobile-01.svg'
import { ReactComponent as GrayBgSvgTwo } from '../../assets/svgs/clayville-collagebgshapes-mobile-02.svg'
import { ReactComponent as GrayBgSvgThree } from '../../assets/svgs/clayville-collagebgshapes-mobile-03.svg'
import { ReactComponent as GrayBgSvgFour } from '../../assets/svgs/clayville-collagebgshapes-mobile-04.svg'
import { ReactComponent as GrayBgSvgFive } from '../../assets/svgs/clayville-collagebgshapes-mobile-05.svg'

import { ReactComponent as CollageBorder } from '../../assets/svgs/clayville-collage-border-01.svg'

const StoryCollageMobile = ({ index, storyCollageHeader, storyCollageItems, color, isMobile = false, inActive = null, locationName, onSetActiveSectionNum }) => {
    const [isIntersecting, setIsIntersecting] = useState(false)
    const ref = useRef(null)

    const observer = useMemo(() =>
        new IntersectionObserver(([entry]) => setIsIntersecting(entry.isIntersecting), { threshold: 0.5 }
        ), [])

    useEffect(() => {
        observer.observe(ref.current)
        return () => {
            observer.disconnect()
        }
    }, [ref, observer])

    useEffect(() => {
        if (isIntersecting && onSetActiveSectionNum) {
            onSetActiveSectionNum(index)
        }
    }
    , [index, isIntersecting])

    const renderCollageHeader = () => {
        let headerParts = []
        let collageHeader = storyCollageHeader || ''
        let clayvilleLink = <Link key="link-1" to={routes.clayvilleIndex.path} target="_blank">Clayville landmarks</Link>
        let murphysLink = <Link key="link-2" to={routes.murphysIndex.path} target="_blank">Tales from the East Side</Link>
        if (collageHeader.includes('Clayville landmarks')) {
            let parts = collageHeader.split('Clayville landmarks')
            parts.forEach((part, idx) => {
                headerParts.push(part)
                if (idx < parts.length - 1) {
                    headerParts.push(clayvilleLink)
                }
            })
        }
        if (collageHeader.includes('Tales from the East Side')) {
            if (!headerParts.length) {
                let parts = collageHeader.split('Tales from the East Side')
                // headerParts = []
                parts.forEach((part, idx) => {
                    headerParts.push(part)
                    if (idx < parts.length - 1) {
                        headerParts.push(murphysLink)
                    }
                })
            } else {
                let newHeaderParts = []
                headerParts.forEach((part, idx) => {
                    if (typeof part === 'string' && part.includes('Tales from the East Side')) {
                        let parts = part.split('Tales from the East Side')
                        parts.forEach((subPart, subIdx) => {
                            newHeaderParts.push(subPart)
                            if (subIdx < parts.length - 1) {
                                newHeaderParts.push(murphysLink)
                            }
                        })
                    } else {
                        newHeaderParts.push(part)
                    }
                })
                headerParts = newHeaderParts
            }
        }
        return (
            <div className="story-collage-header">
                <h2>{headerParts}</h2>
            </div>
        )
    }

    const renderCollageItems = () => {
        let allItems = []
        storyCollageItems.forEach((item, idx) => {
            let data = item.data
            let type = item.type
            // If we have an SVG, use it, else default to the shape svgs based on the index for visual interest
            let SvgBackground = KiBgSvgOne // default
            let SvgGreyBackground = GrayBgSvgOne // default
            if (idx === 1) {
                SvgBackground = KiBgSvgTwo
                SvgGreyBackground = GrayBgSvgTwo
            } else if (idx === 2) {
                SvgBackground = KiBgSvgThree
                SvgGreyBackground = GrayBgSvgThree
            } else if (idx === 3) {
                SvgBackground = KiBgSvgFour
                SvgGreyBackground = GrayBgSvgFour
            } else if (idx === 4) {
                SvgBackground = KiBgSvgFive
                SvgGreyBackground = GrayBgSvgFive
            }
            let svg = <SvgBackground className={`collage-svg ${data.color ? data.color : ''}`} />
            if (data.svgShape) {
                svg = <img src={data.svgShape.url} alt={`${data.svgShape?.alt} shape`} className="collage-svg" />
            }
            let bg = null
            if (data.background) {
                svg = null
                bg = <img src={data.background.url} alt={`${data.background?.alt} background`} className="collage-bg" />
            }
            let mg = null
            if (data.middleground) {
                mg = <img src={data.middleground.url} alt={`${data.middleground?.alt} middleground`} className="collage-mg" />
            }
            let fg = null
            if (data.foreground) {
                fg = <img src={data.foreground.url} alt={`${data.foreground?.alt} foreground`} className="collage-fg" />
            }
            let itemRoute = null
            if (item.type === 'character') {
                itemRoute = buildRoutePath(routes.murphysView.path, { character: data.alias.substring(1) })
            } else if (item.type === 'location') {
                itemRoute = buildRoutePath(routes.clayvilleView.path, { location: data.alias.substring(1) })
            } else if (item.type === 'key_issue') {
                let cleanAlias = data.alias.replace(/\/key-issues\//, '')
                itemRoute = buildRoutePath(routes.keyIssueOverview.path, { keyIssueSlug: cleanAlias })
            }
            if (type === 'placeholder') {
                allItems.push(
                    <div key={`collage-item-${idx}`} className={`story-collage-item placeholder`}>
                        <SvgGreyBackground className="svg-gray-bg" />
                        <div className="collage-item-header">Placeholder</div>
                        <div className="collage-item-images">
                            { svg }
                        </div>
                    </div>
                )
            } else {
                allItems.push(
                    <a key={`collage-item-${idx}`} className={`story-collage-item ${type}`} href={itemRoute}>
                        <SvgGreyBackground className="svg-gray-bg" />
                        <div className="collage-item-header">{ data.name }</div>
                        <div className="collage-item-images">
                            { svg }
                            { bg }
                            { mg }
                            { fg }
                        </div>
                    </a>
                )
            }
        })
        return allItems
    }

    return (
        <div ref={ref} className="story-collage-wrapper" id={`section-${index}`}>
            <div className="story-collage-border">
                <CollageBorder />
            </div>
            <div className="container">
                { renderCollageHeader() }
            </div>
            <div className="story-collage-items">
                { renderCollageItems() }
            </div>
        </div>
    )
}

export default StoryCollageMobile
